import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { useRouter } from "next/router"
import { isSafari } from "react-device-detect"
import cx from "classnames"
import ProductListUI from "@/components/ProductList/v3/ui/ProductListUI"
import styles from "@/components/ProductList/v3/styles/ProductList.module.scss"
import useSmartCrop from "@/hooks/useSmartCrop"
import {
  reformatFacetString,
  plpFilterArrayFormatter,
  formatFacets,
  plpSortArrayFormatter,
  colorFetcheslogic,
  isPriceRangeFilter,
} from "@/responseProcessors/plpResponseProcessor"
import {
  organizeData,
  getSwatchCount,
  getUserPersona,
  isCanadaSite,
  formatPriceRange,
} from "@/utils/helper"
import { useProductContext } from "@/components/ProductList/v3/ProductListContext"
import useIsSsr from "@/hooks/useIsSsr"
import { getListFromClient } from "@/services/client/plp.client"
import { resourceFacetLabels, DEVICE_TYPE_DESKTOP } from "@/constants/index"
import {
  addBackToTopAnalytics,
  addLoadMoreAnalytics,
  handlePLPDataLayer,
  getPLPPaginationAnalyticsData,
} from "@/components/ProductList/v3/analytics/analytics"
import { getFilterAnalyticsForFacetCheckbox } from "@/components/ProductList/v3/analytics/PLPFilterAnalytics"
import {
  getIsPresentOnDevice,
  getPromoColumns,
  getPromoTilePosition,
} from "@/utils/product"
import { useTranslation } from "next-i18next"
import useWindowResize from "@/hooks/useWindowResize"
import { useDispatch } from "react-redux"
import {
  setCurrentAppliedfiltersVisualNav,
  setServerData,
  setFilterQueryState,
  setIsFromVisualNav,
} from "@/store/features/productListSlice"
import { Spinner } from "@kohler-dsm-library/kohler-core"
import * as indexedDBUtil from "@/utils/indexDB"
import announceForAccessibility from "@/utils/announceForAccessibility"
const propsScroll = {
  type: "auto",
  scrollbars: "y",
  scrollHideDelay: 500,
}

const ProductList = () => {
  const {
    plpLocalState,
    setPlpLocalState,
    setPromoInfo,
    componentProps,
    isAuthenticated,
    deviceType,
    isServerData,
    isFromVisualNav,
    general: {
      siteName = "",
      scene7BaseUrl = "",
      dmScene7AccountName = "",
    } = {},
  } = useProductContext()
  const [width] = useWindowResize()
  const persona = getUserPersona()
  const dispatch = useDispatch()
  const isCanadaCheck = isCanadaSite(siteName)

  // Update swatches using getSwatchCount after all cards/data are loaded (first load)
  useEffect(() => {
    if (!plpLocalState.cardsData || !Array.isArray(plpLocalState.cardsData))
      return
    setPlpLocalState(prev => {
      const allowedColorCount = getSwatchCount(true)
      const swatchUrl = componentProps?.swatchUrl || ""
      const updatedCards = prev.cardsData.map(card => {
        // Use colorFetcheslogic to generate swatch objects like plpResponseProcessor
        const swatches = colorFetcheslogic(
          card.product ? card.product : card,
          width,
          swatchUrl
        )
        let extraColors = 0
        if (swatches.length > allowedColorCount) {
          extraColors = swatches.length - allowedColorCount
        }
        return {
          ...card,
          productDetails: {
            ...card.productDetails,
            colorSwatches: {
              ...card.productDetails.colorSwatches,
              colors: swatches.slice(0, allowedColorCount),
              extraColors,
            },
          },
        }
      })
      return {
        ...prev,
        cardsData: updatedCards,
      }
    })
    // Update on every window resize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width])
  const isSsr = useIsSsr()
  const hasMounted = useRef(false)
  const { t } = useTranslation()
  // Get values from plpLocalState
  const {
    cardsData,
    promoData,
    isGrouped,
    facetLabels,
    selectedFilterArray,
    filterQuery,
    selectSort,
    facets,
    colorLabels,
    defaultProductsCount,
    isLoading,
  } = plpLocalState

  const {
    categoryKey,
    attributevalue,
    attributeName,
    isServiceParts = "",
    presentOnMobile = "",
    repeatPromo = false,
  } = componentProps

  const deviceStatus = useMemo(
    () => getIsPresentOnDevice(presentOnMobile),
    [presentOnMobile]
  )

  const [getIsPresentOnDeviceStatus, setGetIsPresentOnDeviceStatus] =
    useState(deviceStatus)

  const initialPartData = {
    productId: "",
    partId: "",
    isShowModal: false,
  }
  const [partsData, setPartsData] = useState({ ...initialPartData })
  const [svgFileMap, setSvgFileMap] = useState([])
  const [currentPerPage, setCurrentPerPage] = useState(defaultProductsCount)
  const [linkCopied, setLinkCopied] = useState(false)
  const [pageState, setPageState] = useState("")
  const [refreshFilters, setRefreshFilters] = useState(0)
  const [announceResultsAfterClear, setAnnounceResultsAfterClear] =
    useState(false)
  const [statusMessage, setStatusMessage] = useState("")
  const smartCrop = useSmartCrop()
  const router = useRouter()

  const [rowCols, setRowCols] = useState(width < 1024 ? 2 : 3)
  const [isFilterVisible, setIsFilterVisible] = useState(
    deviceType === DEVICE_TYPE_DESKTOP
  )
  const [isShareCTA, setIsShareCTA] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)

  // calculate promo position
  const [promoCols, setPromoCols] = useState(getPromoColumns(width))
  const isMobileViewport = width < 1024

  const colVal = { "one-column": 1, "two-column": 2, "three-column": 3 }

  const position =
    width > 0 && componentProps?.promoLayout
      ? getPromoTilePosition(
          parseInt(componentProps.promoPlacementDesktop),
          componentProps?.promoLayout,
          width
        )
      : null

  // When user logged in after client interaction
  useEffect(() => {
    if (isAuthenticated && !isServerData) {
      // User just logged in
      fetchData(
        { facetsString: filterQuery, selectSort },
        0,
        currentPerPage,
        null,
        persona
      )
    }
  }, [isAuthenticated])

  const handlePageShow = e => {
    if (!isSafari || !e.persisted) return

    // Allow Safari to restore scroll first
    setTimeout(() => {
      requestAnimationFrame(() => {
        const scroller = document.scrollingElement
        document.querySelectorAll("img").forEach(img => {
          if (!img.complete && img.src) {
            const src = img.src
            img.src = ""
            img.src = src
          }
        })

        if (!scroller) return
        // 🔥 temporarily nudge the real scroll container
        const prevOverflow = scroller.style.overflow

        scroller.style.overflow = "hidden"
        scroller.offsetHeight // force reflow

        setTimeout(() => {
          scroller.style.overflow = prevOverflow || "auto"
        }, 100)
      })
    }, 500) // 👈 critical delay
  }

  useEffect(() => {
    document.addEventListener("click", handlePLPDataLayer)
    return () => {
      document.removeEventListener("click", handlePLPDataLayer)
    }
  }, [])
  const isRestoringFromPDP = useRef(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const clearPlpLoader = async () => {
      setPlpLocalState(prev =>
        prev?.isLoading ? { ...prev, isLoading: false } : prev
      )
      try {
        await indexedDBUtil.removeItem("fromPDP")
      } catch (e) {
        /* ignore errors from storage */
      }
    }
    const onPageShow = e => {
      if (e && e.persisted) {
        clearPlpLoader()
        if (width < 1024) {
          handlePageShow(e)
        }
      } else {
        const overlay = document.querySelector(".spinner-overlay")
        if (overlay) clearPlpLoader()
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const overlay = document.querySelector(".spinner-overlay")
        if (overlay) clearPlpLoader()
      }
    }
    const onPopState = () => {
      const overlay = document.querySelector(".spinner-overlay")
      if (overlay) clearPlpLoader()
    }
    window.addEventListener("pageshow", onPageShow)
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("popstate", onPopState)
    return () => {
      window.removeEventListener("pageshow", onPageShow)
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("popstate", onPopState)
    }
  }, [setPlpLocalState])

  useEffect(() => {
    let timeoutId

    if (typeof window !== "undefined") {
      // Update indexDB and fetch data
      const fetchDataAsync = async () => {
        const fromPDP = await indexedDBUtil.getItem("fromPDP")
        const plpCardsData = await indexedDBUtil.getItem(
          `plpCardsData+${window.location.href}`
        )
        const plpCardsDataLength = await indexedDBUtil.getItem(
          `plpCardsDataLength+${window.location.pathname}`
        )
        const isServerSideData = await indexedDBUtil.getItem("isServerData")

        if (fromPDP && plpCardsData) {
          isRestoringFromPDP.current = true
          // Run your logic here (e.g., scroll to product, reload, etc.)
          setPlpLocalState(prev => ({
            ...prev,
            isLoading: true,
            cardsData: JSON.parse(plpCardsData),
            plpData: JSON.parse(plpCardsData),
          }))
          setCurrentPerPage(plpCardsDataLength)
          dispatch(setServerData(isServerSideData === "true"))
          setPageState("")
          timeoutId = setTimeout(async () => {
            await indexedDBUtil.removeItem("fromPDP")
            await indexedDBUtil.removeItem(
              `plpCardsData+${window.location.href}`
            )
            await indexedDBUtil.removeItem("plpsku")
            setPlpLocalState(prev => ({
              ...prev,
              isLoading: false,
            }))
            setTimeout(() => {
              isRestoringFromPDP.current = false
            }, 1000)
          }, 500)
        } else {
          await indexedDBUtil.setItem(
            `plpCardsDataLength+${window.location.pathname}`,
            30
          )
        }
      }
      fetchDataAsync()
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])

  /**
   * based on width, we will set the rowCols and promoCols
   */
  useEffect(() => {
    if (width < 1024) {
      setRowCols(2)
      setIsFilterVisible(false)
      // If the width is less than or equal to 1280, we will limit the color swatches to 5
      // Update cardsData through plpLocalState
      setPlpLocalState(prev => {
        const updatedCards = prev.cardsData?.map(card => {
          const colors = card?.productDetails?.colorSwatches?.colors
          if (colors?.length > 5) {
            return {
              ...card,
              productDetails: {
                ...card.productDetails,
                colorSwatches: {
                  ...card.productDetails.colorSwatches,
                  colors: colors.slice(0, -2),
                  extraColors: colors.length - 5,
                },
              },
            }
          }
          return card
        })
        return {
          ...prev,
          cardsData: updatedCards,
        }
      })
    } else {
      setRowCols(3)
      setIsFilterVisible(true)
    }
    setPromoCols(getPromoColumns(width))
  }, [width])

  useEffect(() => {
    setGetIsPresentOnDeviceStatus(
      getIsPresentOnDevice(componentProps?.presentOnMobile)
    )
  }, [componentProps?.presentOnDesktop, componentProps?.presentOnMobile])

  const filterClass = cx({
    "product-listing__filter": true,
    "product-listing__filter--hide": !isFilterVisible,
    "product-listing__filter--modal": isFilterVisible,
  })

  const tileClass = cx({
    "product-listing__tile-col": true,
    "product-listing__tile-col--full-width": !isFilterVisible,
  })

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    // Move focus to the top element
    setTimeout(() => {
      // Focus on the product list wrapper or its parent heading
      const productListElement =
        document.getElementsByClassName("visualNavWrapper")[0] ??
        document.getElementsByClassName("productListWrapper")[0]

      if (productListElement) {
        productListElement.setAttribute("tabindex", "-1")
        productListElement.focus({ preventScroll: true })

        // Clean up tabindex after blur
        productListElement.addEventListener(
          "blur",
          () => {
            productListElement.removeAttribute("tabindex")
          },
          { once: true }
        )
      }
    }, 500)
  }

  const propsPagination = {
    mod: "white",
    ...(plpLocalState.plpTotal > 30 && {
      viewLabel: t("kf.plp.label.view"),
      currentPerPage: currentPerPage,
      perPageCTAs: [
        {
          value: 30,
          "aria-label":
            t("kf.plp.label.view") + " 30 " + t("kf.plp.label.results"),
        },
        {
          value: 60,
          "aria-label":
            t("kf.plp.label.view") + " 60 " + t("kf.plp.label.results"),
        },
        {
          value: 90,
          "aria-label":
            t("kf.plp.label.view") + " 90 " + t("kf.plp.label.results"),
        },
      ],
      onPerPageClicked: e => {
        // Check if new value is less than previous value
        if (e < currentPerPage) {
          // Scroll to top of product list
          if (typeof window !== "undefined") {
            setTimeout(() => {
              const productListElement =
                document.getElementsByClassName(
                  "kohler-visual-filter-root"
                )[0] ?? document.getElementsByClassName("productListWrapper")[0]
              if (productListElement) {
                productListElement.scrollIntoView({ behavior: "smooth" })
              }
            }, 100)
          }
        }

        setCurrentPerPage(e)
        indexedDBUtil.setItem(
          `plpCardsDataLength+${window.location.pathname}`,
          e
        )
        setPageState("pagination")
        fetchData(
          { facetsString: filterQuery, selectSort },
          0,
          e,
          null,
          persona
        )
        dispatch(setServerData(false))
        getPLPPaginationAnalyticsData(e)
      },
    }),
    backToTopCTA: {
      label: t("kf.plp.label.backToTop"),
      "aria-label": "Back to results",
      onClick: () => {
        handleBackToTop()
        addBackToTopAnalytics(false, "plp")
      },
    },
    ...(plpLocalState.plpTotal > 30 &&
      plpLocalState?.plpTotal !== cardsData?.length && {
        loadMoreCTA: {
          text: t("kf.plp.label.loadMore"),
          "aria-label": `${t("kf.plp.label.loadMore")} Products`,
          onClick: () => {
            fetchData(
              { facetsString: filterQuery, selectSort },
              cardsData.length,
              "30",
              "loadMore",
              persona
            )
            dispatch(setServerData(false))
            setCurrentPerPage(30 + cardsData.length)
            setPageState("loadMore")
            addLoadMoreAnalytics("plp")
          },
        },
      }),
  }
  useEffect(() => {
    setGetIsPresentOnDeviceStatus(
      getIsPresentOnDevice(componentProps?.presentOnMobile)
    )
  }, [componentProps])

  const onCloseModal = () => {
    setPartsData({
      ...initialPartData,
      isShowModal: false,
    })
    if (scrollPosition) {
      window.scrollTo({
        top: parseInt(scrollPosition, 10),
        behavior: "instant",
      })
    }
    setScrollPosition(0)
  }

  const handleOnClickPartsDiagramLink = useCallback(data => {
    const scrollY = window?.scrollY
    const getPartId = data.resourceName
    const resourceTitles = data.resourceTitle
    const partId = Array.isArray(getPartId) ? getPartId[0] : getPartId

    setPartsData({
      partId,
      productId: data?.productNo,
      isShowModal: true,
    })

    const partLinks = organizeData(
      getPartId,
      resourceTitles,
      "Service Parts Diagram",
      "PART"
    )
    setSvgFileMap(partLinks)
    setScrollPosition(scrollY)
  }, [])

  useEffect(() => {
    if (pageState === "loadMore") {
      indexedDBUtil.setItem(
        `plpCardsDataLength+${window.location.pathname}`,
        currentPerPage
      )
    }
  }, [pageState, currentPerPage])

  const fetchData = useCallback(
    async (
      { facetsString, selectSort },
      start = 0,
      rows = 30,
      perpageclick = null,
      persona
    ) => {
      const params = {
        fq: [
          ...reformatFacetString(
            facetsString,
            facetLabels,
            persona,
            isCanadaCheck
          ),
        ],
        rows: rows,
        start: start,
        fl: resourceFacetLabels.join(","),
        sort: selectSort,
      }
      const clientData = await getListFromClient(
        categoryKey,
        params,
        isServiceParts,
        plpLocalState?.plpServletPath
      )
      if (perpageclick === "loadMore") {
        setPlpLocalState(prev => ({
          ...prev,
          cardsData: [
            ...prev.cardsData,
            ...(clientData?.transformedCards || []),
          ],
          plpData: [...prev.plpData, ...(clientData?.data || [])],
          plpTotal: clientData?.total,
          facets: clientData?.facets,
          colorLabels: clientData?.colors,
          facetLabels: clientData?.facetLabels,
        }))
      } else {
        setPlpLocalState(prev => ({
          ...prev,
          cardsData: [...(clientData?.transformedCards || [])],
          plpData: [...(clientData?.data || [])],
          plpTotal: clientData?.total,
          facets: clientData?.facets,
          colorLabels: clientData?.colors,
          facetLabels: clientData?.facetLabels,
        }))
      }
    },
    [
      filterQuery,
      resourceFacetLabels,
      categoryKey,
      isServiceParts,
      plpLocalState?.plpServletPath,
      getListFromClient,
      setPlpLocalState,
      reformatFacetString,
    ]
  )

  const isServerDataRef = useRef(isServerData)
  const isFromCheckBoxClickedRef = useRef(true)

  useEffect(() => {
    isServerDataRef.current = isServerData
  }, [isServerData])

  const handleServerSidedataOnchange = useCallback(() => {
    if (isServerDataRef.current) {
      isServerDataRef.current = false
      dispatch(setServerData(false))
    }
  }, [dispatch])

  const handleActiveFiltersChange = useCallback(newFilters => {
    setPlpLocalState(prev => {
      const prevFilters = prev.selectedFilterArray || []
      const added = newFilters.filter(x => !prevFilters.includes(x))
      const removed = prevFilters.filter(x => !newFilters.includes(x))
      const actionType = added.length > 0 ? "applied" : "removed"
      const changedFilter = added.length > 0 ? added[0] : removed[0]

      const filterArray = newFilters
        .map(item => {
          const [facet, value] = item.split(" - ")
          return `${
            isPriceRangeFilter(facet)
              ? t("kf.plp.label.priceRangeTitle")
              : facetLabels[facet]
          }:${isPriceRangeFilter(facet) ? formatPriceRange(value) : value}`
        })
        .join("|")

      // Only send analytics if something actually changed
      if (
        changedFilter &&
        isFromCheckBoxClickedRef.current &&
        !isServerDataRef.current
      ) {
        const [facet, value] = changedFilter.split(" - ")

        getFilterAnalyticsForFacetCheckbox(
          actionType,
          isPriceRangeFilter(facet) ? formatPriceRange(value) : value,
          isPriceRangeFilter(facet)
            ? t("kf.plp.label.priceRangeTitle")
            : facetLabels[facet],
          filterArray
        )
      }

      if (
        JSON.stringify(prev.selectedFilterArray) !== JSON.stringify(newFilters)
      ) {
        dispatch(setCurrentAppliedfiltersVisualNav(newFilters))

        return {
          ...prev,
          selectedFilterArray: newFilters,
          filterQuery: formatFacets(
            newFilters,
            facetLabels,
            attributeName,
            attributevalue
          ),
        }
      }
      return prev
    })
  }, [])

  useMemo(() => {
    let timeoutId
    if (typeof window !== "undefined" && !isServerData) {
      // Update indexDB and fetch data
      const fetchDataAsync = async () => {
        const fromPDP = await indexedDBUtil.getItem("fromPDP")
        const url = new URL(window.location.href)
        if (
          !fromPDP &&
          !isRestoringFromPDP.current &&
          (url.searchParams.has("sort") ||
            plpLocalState.selectSort === "featured" ||
            (selectedFilterArray.length === 0 && pageState === "clearAll") ||
            url.searchParams.has("facets"))
        ) {
          timeoutId = setTimeout(() => {
            const productListElement =
              document.getElementsByClassName("visualNavWrapper")[0] ??
              document.getElementsByClassName("productListWrapper")[0]
            if (productListElement) {
              productListElement.scrollIntoView({ behavior: "smooth" })
            }
          }, 10)
        }
      }
      fetchDataAsync()
    }
    return () => {
      clearTimeout(timeoutId)
    }
  }, [router, isServerData])

  // Helper to update the URL with only filter changes
  const updateUrlWithFilters = filterQuery => {
    const query = {}
    if (filterQuery) {
      query.facets = filterQuery
    }
    // Keep current sort in URL if present
    if (selectSort && selectSort !== "featured" && selectSort !== "") {
      query.sort = selectSort
    }
    const baseUrl = router?.asPath?.split("?")[0]
    const searchParams = Object.keys(query).length
      ? "?" + new URLSearchParams(query).toString()
      : ""
    const browserUrl = baseUrl + searchParams
    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      browserUrl,
      { shallow: true }
    )
    isFromVisualNav && setRefreshFilters(v => v + 1)
    dispatch(setIsFromVisualNav(false))
    dispatch(setFilterQueryState(filterQuery))
  }

  // Helper to update the URL with only sort changes
  const updateUrlWithSort = sortValue => {
    const baseUrl = router.asPath.split("?")[0]
    const params = []

    if (filterQuery) {
      params.push(`facets=${encodeURIComponent(filterQuery)}`)
    }
    if (sortValue && sortValue !== "featured" && sortValue !== "") {
      params.push(`sort=${sortValue}`)
    }

    const newUrl =
      params.length > 0 ? `${baseUrl}?${params.join("&")}` : baseUrl
    const urlWithLocale = `/${router.locale}${newUrl}`
    router.replace(urlWithLocale, undefined, { shallow: true })
  }

  useEffect(() => {
    if (isSsr) return
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    // Update indexDB and fetch data
    const fetchDataAsync = async () => {
      const plpCardsDataLength = await indexedDBUtil.getItem(
        `plpCardsDataLength+${window.location.pathname}`
      )
      const rows = plpCardsDataLength || 30
      if (!isServerData && selectedFilterArray) {
        fetchData(
          { facetsString: filterQuery, selectSort },
          0,
          rows,
          null,
          persona
        )
        updateUrlWithFilters(filterQuery)
      } else if (!isServerData && selectSort) {
        fetchData(
          { facetsString: filterQuery, selectSort },
          0,
          rows,
          null,
          persona
        )
        updateUrlWithSort(selectSort)
      }
      setCurrentPerPage(rows)
    }
    fetchDataAsync()
  }, [selectSort, isSsr, filterQuery])

  // Announce results count after the "cleared all filters" announcement completes
  useEffect(() => {
    const countMessage = `${plpLocalState.plpTotal} ${
      t("kf.search.label.results") || "results"
    }`
    announceForAccessibility(countMessage)
    if (announceResultsAfterClear) {
      // Wait for the first announcement to finish (1200ms) plus small buffer for SR
      const timeoutId = setTimeout(() => {
        const countMessage =
          t("kf.plp.announce.clearAllSuccess") ||
          "Cleared all filters successfully"
        announceForAccessibility(countMessage)
        // Reset flag so it doesn't keep announcing
        setAnnounceResultsAfterClear(false)
      }, 2000)

      return () => clearTimeout(timeoutId)
    }
  }, [plpLocalState.plpTotal])

  // handle for sort
  const propsSortUI = useMemo(
    () =>
      plpSortArrayFormatter(
        plpLocalState,
        t,
        isFilterVisible,
        setIsFilterVisible,
        selectSort,
        isGrouped,
        setPlpLocalState,
        isMobileViewport,
        dispatch,
        setServerData
      ),
    [plpLocalState, isFilterVisible, selectSort, isGrouped, isMobileViewport]
  )

  // Handle for Filter
  const propsFilters = useMemo(
    () =>
      plpFilterArrayFormatter(
        plpLocalState,
        isFilterVisible,
        setIsFilterVisible,
        componentProps,
        t,
        handleServerSidedataOnchange,
        handleActiveFiltersChange,
        linkCopied,
        setLinkCopied,
        refreshFilters,
        isFromVisualNav,
        isFromCheckBoxClickedRef,
        dispatch,
        setFilterQueryState,
        setIsFromVisualNav,
        setPageState,
        isMobileViewport,
        setAnnounceResultsAfterClear,
        isShareCTA,
        setIsShareCTA
      ),
    [
      facets,
      facetLabels,
      filterQuery,
      colorLabels,
      isFilterVisible,
      linkCopied,
      refreshFilters,
      isShareCTA,
    ]
  )

  // if the total count changes, we want to update the status message for screen readers
  const prevPlpTotalRef = useRef(plpLocalState.plpTotal)

  useEffect(() => {
    if (plpLocalState.plpTotal !== prevPlpTotalRef.current) {
      prevPlpTotalRef.current = plpLocalState.plpTotal
      setStatusMessage(`${plpLocalState.plpTotal} ${t("kf.plp.label.results")}`)
    } else {
      setStatusMessage("")
    }
  }, [plpLocalState.plpTotal])
  return (
    <>
      {isLoading && (
        <div className="spinner-overlay">
          <Spinner mod="white" />
        </div>
      )}

      <span
        role="alert"
        aria-live="polite"
        className="sr-only"
        aria-label={`${statusMessage}`}
      />

      <div
        data-testid="product-list-wrapper"
        className={styles.productListWrapper}
      >
        <div
          id="site-announcer"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={styles.siteAnnouncer}
        />
        <ProductListUI
          propsPagination={propsPagination}
          propsSortUI={propsSortUI}
          propsScroll={propsScroll}
          propsFilters={propsFilters}
          propsProductCard={cardsData}
          filterClass={filterClass}
          tileClass={tileClass}
          rowCols={rowCols}
          width={width}
          smartCrop={smartCrop}
          componentProps={componentProps}
          getIsPresentOnDeviceStatus={getIsPresentOnDeviceStatus}
          setPromoData={setPromoInfo}
          colVal={colVal}
          promoData={promoData}
          position={position}
          promoCols={promoCols}
          partData={partsData}
          onCloseModal={onCloseModal}
          svgFileMap={svgFileMap}
          totalCount={plpLocalState.plpTotal}
          handleOnClickPartsDiagramLink={handleOnClickPartsDiagramLink}
          isMobileViewport={isMobileViewport}
          repeatPromo={repeatPromo}
          scene7BaseUrl={scene7BaseUrl}
          dmScene7AccountName={dmScene7AccountName}
        />
      </div>
    </>
  )
}

export default ProductList
